import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import baseTheme, { BaseTheme } from "@insite/mobius/globals/baseTheme";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Typography from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { COLORS } from "@tCentro/theme/design";
import React, { FC } from "react";
import { useSelector } from "react-redux";
import { css } from "styled-components";

const enum fields {
    copyrightText = "copyrightText",
    logoImage = "logoImage",
    textColor = "textColor",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.copyrightText]: string;
        [fields.logoImage]: string;
        [fields.textColor]: string;
    };
}

export interface VideoCardStyles {
    containerWrapper?: InjectableCss;
    copyrightText?: InjectableCss;
    image?: InjectableCss;
}

export const videoCardStyles: VideoCardStyles = {
    containerWrapper: {
        css: css`
            display: flex;
            align-items: center;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            flex-direction: column;
                        `,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    image: {
        css: css`
            width: 59px;
        `,
    },
    copyrightText: {
        css: css`
            font-weight: 400;
            font-size: 14px;
            line-height: 20px;
            @media (min-width: 1170px) {
                font-size: 14px !important;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            padding-top: 16px;
                            font-size: 14px;
                        `,
                        null,
                        css`
                            padding-top: 0;
                            padding-left: 16px;
                            font-size: 10px;
                        `,
                        css`
                            font-size: 12px;
                        `,
                    ],
                    "min",
                )}
        `,
    },
};

const styles = videoCardStyles;
export const VideoWidget: FC<OwnProps> = ({ fields }) => {
    const websiteName = useSelector((state: ApplicationState) => state.context?.website?.name || "Centro");

    return (
        <StyledWrapper className="video-wrapper" {...styles.containerWrapper}>
            <LazyImage
                src={fields.logoImage}
                {...styles.image}
                altText={`${websiteName}, Inc.`}
            />
            <Typography {...styles.copyrightText} style={{ color: fields.textColor }}>
                {translate("{0}", fields.copyrightText)}
            </Typography>
        </StyledWrapper>
    );
};

const videoWidget: WidgetModule = {
    component: VideoWidget,
    definition: {
        group: "Basic",
        icon: "image-landscape",
        fieldDefinitions: [
            {
                name: fields.logoImage,
                fieldType: "General",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                displayName: "Logo Image",
            },

            {
                fieldType: "Translatable",
                name: fields.copyrightText,
                editorTemplate: "TextField",
                displayName: "Copyright Text",
                defaultValue: "",
            },
            {
                name: fields.textColor,
                fieldType: "General",
                editorTemplate: "ColorPickerField",
                defaultValue: `${getColor("text.main")}`,
                displayName: "Text Color",
            },
        ],
    },
};

export default videoWidget;
